/**
 * Settings > Permissions
 *
 * Per-role module access matrix. Admin row is locked (admin always sees
 * everything; toggles are disabled there). Toggling cells flips the
 * pending change set; "Save" PUTs the partial diff.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { permissionsApi } from '../../../api/client';
import HelpButton from '../../help/HelpButton';

const ROLES = ['admin', 'supervisor', 'agent', 'viewer'];

export default function PermissionsPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'permissions'],
    queryFn: () => permissionsApi.matrix(),
  });

  // pending[role][module_key] = bool (overrides server value)
  const [pending, setPending] = useState({});
  useEffect(() => { setPending({}); }, [data]);

  const save = useMutation({
    mutationFn: () => permissionsApi.updateMatrix(pending),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'permissions'] });
      qc.invalidateQueries({ queryKey: ['permissions', 'me'] });
      setPending({});
    },
  });

  const grouped = useMemo(() => {
    if (!data?.modules) return [];
    const groups = {};
    for (const m of data.modules) {
      (groups[m.group] = groups[m.group] || []).push(m);
    }
    return Object.entries(groups).map(([group, modules]) => ({ group, modules }));
  }, [data]);

  if (isLoading) return <div className="p-6 text-sm text-surface-muted">Loading…</div>;
  if (error)     return <div className="p-6 text-sm text-red-600">{String(error)}</div>;

  const cellValue = (role, mk) => {
    if (pending[role] && mk in pending[role]) return pending[role][mk];
    return !!(data.matrix?.[role]?.[mk]);
  };
  const isDirty = (role, mk) =>
    pending[role] && mk in pending[role] && pending[role][mk] !== !!data.matrix?.[role]?.[mk];

  const toggle = (role, mk) => {
    if (role === 'admin') return; // never disable admin
    setPending((p) => ({
      ...p,
      [role]: { ...(p[role] || {}), [mk]: !cellValue(role, mk) },
    }));
  };

  const totalChanges = Object.values(pending).reduce(
    (n, mods) => n + Object.keys(mods).length, 0
  );

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">Per-role module access</h2>
          <p className="text-sm text-surface-muted dark:text-surface-dark-muted">
            Toggle which sidebar modules each role can see. Admins always see everything.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton moduleKey="settings_permissions" />
          <button
            disabled={!totalChanges || save.isPending}
            onClick={() => save.mutate()}
            className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 disabled:opacity-40 text-white text-sm"
          >
            {save.isPending ? 'Saving…' : `Save changes${totalChanges ? ` (${totalChanges})` : ''}`}
          </button>
        </div>
      </div>

      {save.error && (
        <div className="mb-3 text-sm text-red-600">Save failed: {String(save.error.detail || save.error.message)}</div>
      )}

      <div className="border border-surface-border dark:border-surface-dark-border rounded-thin overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-subtle dark:bg-surface-dark-subtle">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Module</th>
              {ROLES.map((r) => (
                <th key={r} className="px-3 py-2 text-center font-medium capitalize">{r}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grouped.map(({ group, modules }) => (
              <React.Fragment key={group}>
                <tr className="bg-surface-subtle/50 dark:bg-surface-dark-subtle/50">
                  <td colSpan={ROLES.length + 1} className="px-3 py-1 text-xs uppercase tracking-wider text-surface-muted dark:text-surface-dark-muted">
                    {group}
                  </td>
                </tr>
                {modules.map((m) => (
                  <tr key={m.key} className="border-t border-surface-border dark:border-surface-dark-border">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span>{m.icon}</span>
                        <span>{m.label}</span>
                      </div>
                      <div className="text-xs text-surface-muted dark:text-surface-dark-muted">{m.description}</div>
                    </td>
                    {ROLES.map((role) => {
                      const v = cellValue(role, m.key);
                      const dirty = isDirty(role, m.key);
                      const locked = role === 'admin';
                      return (
                        <td key={role} className="px-3 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => toggle(role, m.key)}
                            disabled={locked}
                            title={locked ? 'Admin always has access' : (v ? 'Allowed — click to revoke' : 'Denied — click to grant')}
                            aria-checked={v}
                            role="switch"
                            className={
                              'relative inline-flex items-center align-middle h-6 w-11 shrink-0 rounded-full p-0.5 transition-colors duration-150 ' +
                              (v
                                ? 'bg-continuum-500 '
                                : 'bg-gray-300 dark:bg-gray-600 '
                              ) +
                              (dirty ? 'ring-2 ring-amber-400 ' : '') +
                              (locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-90')
                            }
                          >
                            <span
                              aria-hidden="true"
                              className={
                                'inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-150 ' +
                                (v ? 'translate-x-5' : 'translate-x-0')
                              }
                            />
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-surface-muted dark:text-surface-dark-muted mt-3">
        Tip: amber ring = pending change, not yet saved. Use Save to persist.
      </p>
    </div>
  );
}
